// Ensure Expo sees a full Web Storage implementation even when Node provides a stub.
(() => {
  const globalRef = typeof globalThis !== 'undefined' ? globalThis : global;

  const createMemoryStorage = () => {
    let store = new Map();

    const storage = {
      getItem(key) {
        const value = store.get(String(key));
        return value === undefined ? null : value;
      },
      setItem(key, value) {
        store.set(String(key), String(value));
      },
      removeItem(key) {
        store.delete(String(key));
      },
      clear() {
        store.clear();
      },
      key(index) {
        return Array.from(store.keys())[Number(index)] ?? null;
      },
      get length() {
        return store.size;
      },
      toString() {
        return '[object Storage]';
      },
      get [Symbol.toStringTag]() {
        return 'Storage';
      },
    };

    return storage;
  };

  const ensureStorage = (targetKey) => {
    const descriptor = Object.getOwnPropertyDescriptor(globalRef, targetKey);
    const existing =
      descriptor && typeof descriptor.get === 'function'
        ? descriptor.get.call(globalRef)
        : globalRef[targetKey];

    if (
      existing &&
      typeof existing.getItem === 'function' &&
      typeof existing.setItem === 'function' &&
      typeof existing.removeItem === 'function' &&
      typeof existing.clear === 'function' &&
      typeof existing.key === 'function' &&
      typeof existing.length === 'number'
    ) {
      return existing;
    }

    let storage = createMemoryStorage();

    Object.defineProperty(globalRef, targetKey, {
      configurable: true,
      enumerable: true,
      get() {
        return storage;
      },
      set(value) {
        if (
          value &&
          typeof value.getItem === 'function' &&
          typeof value.setItem === 'function'
        ) {
          storage = value;
        }
      },
    });

    return storage;
  };

  const localStoragePolyfill = ensureStorage('localStorage');
  const sessionStoragePolyfill = ensureStorage('sessionStorage');

  if (typeof window !== 'undefined') {
    try {
      Object.defineProperty(window, 'localStorage', {
        configurable: true,
        enumerable: true,
        get: () => localStoragePolyfill,
        set: (value) => {
          if (
            value &&
            typeof value.getItem === 'function' &&
            typeof value.setItem === 'function'
          ) {
            Object.defineProperty(globalRef, 'localStorage', {
              configurable: true,
              enumerable: true,
              get: () => value,
              set: () => {},
            });
          }
        },
      });

      Object.defineProperty(window, 'sessionStorage', {
        configurable: true,
        enumerable: true,
        get: () => sessionStoragePolyfill,
        set: (value) => {
          if (
            value &&
            typeof value.getItem === 'function' &&
            typeof value.setItem === 'function'
          ) {
            Object.defineProperty(globalRef, 'sessionStorage', {
              configurable: true,
              enumerable: true,
              get: () => value,
              set: () => {},
            });
          }
        },
      });
    } catch {
      window.localStorage = localStoragePolyfill;
      window.sessionStorage = sessionStoragePolyfill;
    }
  } else if (typeof globalRef.window === 'object') {
    globalRef.window.localStorage = localStoragePolyfill;
    globalRef.window.sessionStorage = sessionStoragePolyfill;
  }
})();

